import React, { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL } from "../../environment";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import { getLifecycleTypeName } from "../../utils/lifecycleUtils";

interface RelatedFilesProps {
  companyId?: number | string | null;
}

interface LinkedFile {
  id: number;
  title: string;
  url?: string;
  size_in_kb?: number;
  related_entity_name: string;
  related_entity_id: number;
  category: string;
  description?: string;
  content?: string;
}

interface LinkedEntityResponse {
  attachments: any[];
  notes: any[];
  documents: any[];
  note_attachments: any[];
  document_attachments: any[];
}

const ENTITY_ROUTE_MAP: Record<string, string> = {
  lead: "/leads",
  deal: "/deals",
  company: "/companies",
  contact: "/contacts",
  pipeline: "/pipelines",
  proposal: "/proposals",
  contract: "/contracts",
};

const CATEGORY_STYLES: Record<string, { bg: string; color: string }> = {
  Attachment: { bg: "#e8f5e9", color: "#2e7d32" },
  Note: { bg: "#fff3e0", color: "#e65100" },
  Document: { bg: "#e3f2fd", color: "#1565c0" },
  "Note Attachment": { bg: "#fce4ec", color: "#c62828" },
  "Document Attachment": { bg: "#f3e5f5", color: "#6a1b9a" },
};

const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "attachment", label: "Attachment" },
  { value: "note_attachment", label: "Note Attachment" },
  { value: "document_attachment", label: "Document Attachment" },
];

const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "image", label: "Image (All)" },
  { value: "jpg", label: "JPG Image" },
  { value: "jpeg", label: "JPEG Image" },
  { value: "png", label: "PNG Image" },
  { value: "gif", label: "GIF Image" },
  { value: "webp", label: "WebP Image" },
  { value: "bmp", label: "BMP Image" },
  { value: "svg", label: "SVG Vector" },
  { value: "ico", label: "Icon" },
  { value: "tiff", label: "TIFF Image" },
  { value: "tif", label: "TIF Image" },
  { value: "pdf", label: "PDF Document" },
  { value: "xlsx", label: "Excel File (xlsx)" },
  { value: "xls", label: "Excel File (xls)" },
  { value: "docx", label: "Word File (docx)" },
  { value: "doc", label: "Word File (doc)" },
  { value: "pptx", label: "PowerPoint File (pptx)" },
  { value: "ppt", label: "PowerPoint File (ppt)" },
  { value: "odt", label: "OpenDocument Text" },
  { value: "ods", label: "OpenDocument Spreadsheet" },
  { value: "odp", label: "OpenDocument Presentation" },
  { value: "txt", label: "Text File" },
  { value: "csv", label: "CSV File" },
];

const RelatedFiles: React.FC<RelatedFilesProps> = ({ companyId }) => {
  const [files, setFiles] = useState<LinkedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  const dynamicEntityOptions = [
    { value: "", label: "All Entities" },
    { value: "lead", label: getLifecycleTypeName("LEAD") },
    { value: "deal", label: getLifecycleTypeName("DEAL") },
    { value: "pipeline", label: getLifecycleTypeName("PIPELINE") },
    { value: "proposal", label: getLifecycleTypeName("PROPOSAL") },
    { value: "contract", label: getLifecycleTypeName("CONTRACT") },
  ];

  const [titleFilter, setTitleFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const entityDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const authHeader = localStorage.getItem("authCred");
  const tenantId = localStorage.getItem("selectedTenantId");

  const { hasBeenActivated } = useTabVisibility({
    tabId: "related-files",
    onTabActivated: () => {
      if (!dataLoaded && companyId && tenantId) {
        fetchLinkedFiles();
        setDataLoaded(true);
      }
    },
  });

  useEffect(() => {
    if (hasBeenActivated && !dataLoaded && companyId && tenantId) {
      fetchLinkedFiles();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, companyId, tenantId]);

  useEffect(() => {
    if (!dataLoaded) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchLinkedFiles();
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [titleFilter, entityFilter, categoryFilter, typeFilter]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        entityDropdownRef.current &&
        !entityDropdownRef.current.contains(e.target as Node)
      ) {
        setShowEntityDropdown(false);
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(e.target as Node)
      ) {
        setShowCategoryDropdown(false);
      }
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(e.target as Node)
      ) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchLinkedFiles = useCallback(async () => {
    if (!companyId || !tenantId || !authHeader) return;
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/lookup/linked-entity-data/?tenant_id=${tenantId}&related_entity_id=${companyId}&related_entity_name=company`;
      if (titleFilter) url += `&title=${encodeURIComponent(titleFilter)}`;
      if (entityFilter) url += `&related_entity=${encodeURIComponent(entityFilter)}`;
      if (categoryFilter) url += `&category=${encodeURIComponent(categoryFilter)}`;
      if (typeFilter) url += `&type=${encodeURIComponent(typeFilter)}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${authHeader}`,
        },
      });

      if (!response.ok) throw new Error("Failed to fetch linked files");

      const data: LinkedEntityResponse = await response.json();
      const flatFiles = flattenResponse(data).filter((f) => f.url);
      setFiles(flatFiles);
    } catch (error) {
      console.error("Error fetching linked files:", error);
    } finally {
      setLoading(false);
    }
  }, [companyId, tenantId, authHeader, titleFilter, entityFilter, categoryFilter, typeFilter]);

  const flattenResponse = (data: LinkedEntityResponse): LinkedFile[] => {
    const result: LinkedFile[] = [];

    data.attachments?.forEach((item) => {
      result.push({
        id: item.attachment_id,
        title: item.title,
        url: item.url,
        size_in_kb: item.size_in_kb,
        related_entity_name: item.related_entity_name,
        related_entity_id: item.related_entity_id,
        category: "Attachment",
      });
    });

    data.notes?.forEach((item) => {
      result.push({
        id: item.note_id,
        title: item.title,
        related_entity_name: item.related_entity_name,
        related_entity_id: item.related_entity_id,
        category: "Note",
        description: item.description,
      });
    });

    data.documents?.forEach((item) => {
      result.push({
        id: item.document_id,
        title: item.title,
        related_entity_name: item.related_entity_name,
        related_entity_id: item.related_entity_id,
        category: "Document",
        content: item.content,
      });
    });

    data.note_attachments?.forEach((item) => {
      result.push({
        id: item.note_attachment_id,
        title: item.title,
        url: item.url,
        size_in_kb: item.size_in_kb,
        related_entity_name: item.related_entity_name,
        related_entity_id: item.related_entity_id,
        category: "Note Attachment",
      });
    });

    data.document_attachments?.forEach((item) => {
      result.push({
        id: item.document_attachment_id,
        title: item.title,
        url: item.url,
        size_in_kb: item.size_in_kb,
        related_entity_name: item.related_entity_name,
        related_entity_id: item.related_entity_id,
        category: "Document Attachment",
      });
    });

    return result;
  };

  const truncateTitle = (title: string, max = 20) => {
    if (!title) return "";
    return title.length > max ? title.substring(0, max) + "…" : title;
  };

  const formatSize = (sizeKb?: number) => {
    if (!sizeKb) return "—";
    if (sizeKb >= 1024) return `${(sizeKb / 1024).toFixed(2)} MB`;
    return `${sizeKb.toFixed(2)} KB`;
  };

  const formatEntityName = (name: string) => {
    if (name.toLowerCase() === "lead") return getLifecycleTypeName("LEAD");
    if (name.toLowerCase() === "deal") return getLifecycleTypeName("DEAL");
    if (name.toLowerCase() === "pipeline") return getLifecycleTypeName("PIPELINE");

    return name
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  };

  const handleOpenFile = (file: LinkedFile) => {
    if (file.url) {
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleOpenEntity = (file: LinkedFile) => {
    const basePath = ENTITY_ROUTE_MAP[file.related_entity_name];
    if (basePath) {
      window.open(
        `${basePath}/${file.related_entity_id}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
  };

  const selectedEntityLabel =
    dynamicEntityOptions.find((o) => o.value === entityFilter)?.label ||
    "All Entities";

  const selectedCategoryLabel =
    CATEGORY_OPTIONS.find((o) => o.value === categoryFilter)?.label ||
    "All Categories";

  const selectedTypeLabel =
    TYPE_OPTIONS.find((o) => o.value === typeFilter)?.label ||
    "All Types";

  return (
    <div className="tab-pane fade" id="related-files">
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center justify-content-between">
            <h4 className="fw-semibold">Linked Files</h4>
          </div>
        </div>
        <div className="card-body">
          <div
            className="d-flex align-items-end gap-3 mb-3 flex-wrap"
            style={{ maxWidth: "100%" }}
          >

            <div
              ref={entityDropdownRef}
              style={{
                flex: "0 0 auto",
                minWidth: "170px",
                position: "relative",
              }}
            >
              <label
                className="form-label mb-1"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#6b7280",
                }}
              >
                Related Entity
              </label>
              <button
                type="button"
                className="form-control form-control-sm text-start d-flex align-items-center justify-content-between"
                onClick={() => setShowEntityDropdown(!showEntityDropdown)}
                style={{
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  fontSize: "13px",
                  height: "34px",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>{selectedEntityLabel}</span>
                <i
                  className="ti ti-chevron-down"
                  style={{ fontSize: "14px", color: "#9ca3af" }}
                />
              </button>
              {showEntityDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    minWidth: "100%",
                    zIndex: 1050,
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    marginTop: "4px",
                    overflow: "hidden",
                  }}
                >
                  {dynamicEntityOptions.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setEntityFilter(option.value);
                        setShowEntityDropdown(false);
                      }}
                      style={{
                        padding: "8px 12px",
                        fontSize: "13px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          entityFilter === option.value ? "#f3f4f6" : "#fff",
                        fontWeight:
                          entityFilter === option.value ? 600 : 400,
                        transition: "background-color 0.15s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f9fafb")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        entityFilter === option.value ? "#f3f4f6" : "#fff")
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              ref={categoryDropdownRef}
              style={{
                flex: "0 0 auto",
                minWidth: "170px",
                position: "relative",
              }}
            >
              <label
                className="form-label mb-1"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#6b7280",
                }}
              >
                Category
              </label>
              <button
                type="button"
                className="form-control form-control-sm text-start d-flex align-items-center justify-content-between"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                style={{
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  fontSize: "13px",
                  height: "34px",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>{selectedCategoryLabel}</span>
                <i
                  className="ti ti-chevron-down"
                  style={{ fontSize: "14px", color: "#9ca3af" }}
                />
              </button>
              {showCategoryDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    minWidth: "100%",
                    zIndex: 1050,
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    marginTop: "4px",
                    overflow: "hidden",
                  }}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setCategoryFilter(option.value);
                        setShowCategoryDropdown(false);
                      }}
                      style={{
                        padding: "8px 12px",
                        fontSize: "13px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          categoryFilter === option.value ? "#f3f4f6" : "#fff",
                        fontWeight:
                          categoryFilter === option.value ? 600 : 400,
                        transition: "background-color 0.15s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f9fafb")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        categoryFilter === option.value ? "#f3f4f6" : "#fff")
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              ref={typeDropdownRef}
              style={{
                flex: "0 0 auto",
                minWidth: "170px",
                position: "relative",
              }}
            >
              <label
                className="form-label mb-1"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#6b7280",
                }}
              >
                Type
              </label>
              <button
                type="button"
                className="form-control form-control-sm text-start d-flex align-items-center justify-content-between"
                onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                style={{
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  fontSize: "13px",
                  height: "34px",
                  cursor: "pointer",
                  backgroundColor: "#fff",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>{selectedTypeLabel}</span>
                <i
                  className="ti ti-chevron-down"
                  style={{ fontSize: "14px", color: "#9ca3af" }}
                />
              </button>
              {showTypeDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    minWidth: "100%",
                    zIndex: 1050,
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    marginTop: "4px",
                    maxHeight: "250px",
                    overflowY: "auto",
                  }}
                >
                  {TYPE_OPTIONS.map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setTypeFilter(option.value);
                        setShowTypeDropdown(false);
                      }}
                      style={{
                        padding: "8px 12px",
                        fontSize: "13px",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          typeFilter === option.value ? "#f3f4f6" : "#fff",
                        fontWeight:
                          typeFilter === option.value ? 600 : 400,
                        transition: "background-color 0.15s ease",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = "#f9fafb")
                      }
                      onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        typeFilter === option.value ? "#f3f4f6" : "#fff")
                      }
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {(titleFilter || entityFilter || categoryFilter || typeFilter) && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setTitleFilter("");
                  setEntityFilter("");
                  setCategoryFilter("");
                  setTypeFilter("");
                }}
                style={{
                  height: "34px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: "#6b7280",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  whiteSpace: "nowrap",
                }}
              >
                <i className="ti ti-x" style={{ fontSize: "14px" }} />
                Clear
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="ti ti-files-off"
                style={{ fontSize: "36px", color: "#d1d5db" }}
              />
              <p className="text-muted mt-2 mb-0">No linked files found</p>
            </div>
          ) : (
            <div className="table-responsive custom-table">
              <table className="table table-nowrap mb-0">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Size</th>
                    <th>Related Entity</th>
                    <th>Category</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file, index) => {
                    const catStyle = CATEGORY_STYLES[file.category] || {
                      bg: "#f3f4f6",
                      color: "#374151",
                    };
                    return (
                      <tr key={`${file.category}-${file.id}-${index}`}>
                        <td className="p-3" title={file.title}>
                          {truncateTitle(file.title)}
                        </td>
                        <td className="p-3">{formatSize(file.size_in_kb)}</td>
                        <td className="p-3">
                          <span
                            onClick={() => handleOpenEntity(file)}
                            style={{
                              cursor: "pointer",
                              color: "#4f46e5",
                              fontWeight: 500,
                              textDecoration: "none",
                            }}
                            onMouseEnter={(e) =>
                            (e.currentTarget.style.textDecoration =
                              "underline")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.textDecoration = "none")
                            }
                          >
                            {formatEntityName(file.related_entity_name)}
                          </span>
                        </td>
                        <td className="p-3">
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 10px",
                              borderRadius: "20px",
                              fontSize: "11px",
                              fontWeight: 600,
                              backgroundColor: catStyle.bg,
                              color: catStyle.color,
                              letterSpacing: "0.2px",
                            }}
                          >
                            {file.category}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="d-flex align-items-center gap-2">
                            {file.url && (
                              <>
                                <span style={{ cursor: "pointer", color: "#4f46e5", textDecoration: "none" }} onClick={() => handleOpenFile(file)}>Open File
                                  <i
                                    className="ti ti-external-link cursor-pointer"
                                    style={{
                                      color: "#4f46e5",
                                      fontSize: "16px",
                                      cursor: "pointer",
                                    }}
                                    title="Open file"
                                  /></span>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RelatedFiles;
